export interface ProgramConcentration {
  name: string
  description: string
}

export interface ProgramTemplate {
  slug: string
  /** Slug of the domain this program belongs to. */
  domainSlug: string
  name: string
  /** Short credential label, e.g. "Associate Degree" or "Diploma". */
  credential: string
  metaTitle: string
  metaDescription: string
  heroHeadline: string
  heroSubhead: string
  weeks: number
  credits: number
  instructionalHours: number
  /** Short format note shown in the stat row. */
  format: string
  overview: string[]
  concentrations?: ProgramConcentration[]
  features: string[]
  studentProfile?: string
  careersIntro: string
  careerRoles: string[]
  marketRelevance?: string
}

export interface ProgramDomain {
  slug: string
  name: string
  description: string
}

/**
 * Match-widget context passed through anchor links as
 * ?degreeInterest=&areaStudy=&mProgram= — see `utils/matchParams`.
 * Values come from the option lists in `data/degrees.ts`.
 */
export interface ProgramMatchContext {
  degreeInterest?: string
  areaStudy?: string
  mProgram?: string
}

/** focusOptions value for each domain. */
const domainAreaStudy: Record<string, string> = {
  'business-leadership': '1', // Business & Finance
  'community-population-health': '5', // Healthcare & Medical Assisting
  'data-quality-ai': '5',
  'healthcare-operations': '5',
  'financial-insurance-operations': '5',
  'clinical-care-delivery': '5',
}

/** specializationOptions value for each program. */
const programSpecialization: Record<string, string> = {
  'business-management-associate': '108', // Management
  'community-health-associate': '512', // Public Health
  'healthcare-data-analytics-diploma': '505', // Health Science
  'health-informatics-ai-specialist-associate': '505',
  'healthcare-administrative-operations-specialist-diploma': '506', // Healthcare Administration
  'healthcare-administrative-operations-specialist-associate': '506',
  'medical-insurance-claims-administration-associate': '509', // Medical Billing & Coding
  'revenue-cycle-billing-specialist-associate': '509',
  'radiologic-technology-bridge-associate': '502', // Allied Health
  'clinical-medical-assisting-associate': '500', // Medical Assistant
  'medical-administrative-assistant-associate': '510', // Medical Office Assistant
  'medical-billing-coding-associate': '509', // Medical Billing & Coding
}

/** Diplomas map to Certificate, everything else here is an Associates. */
function degreeInterestFor(credential: string): string {
  return credential.toLowerCase().includes('diploma') ? '1' : '2'
}

/**
 * Hero photography per program, showing the work the program leads to. Shot
 * dark and right-weighted so the navy scrim keeps the headline legible on the
 * left. The two Healthcare Administrative Operations programs share an image.
 */
const programHeroImage: Record<string, string> = {
  'business-management-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/aa3dae32-134c-4725-b746-9e0c1292f384.jpg',
  'community-health-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/ac621744-7cc8-4d97-9961-2c28b1130056.jpg',
  'healthcare-data-analytics-diploma':
    'https://cdn.magicpatterns.com/patterns/generated-images/2466b8dc-20d0-40de-b719-73be78ca2e89.jpg',
  'health-informatics-ai-specialist-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/55b8c463-8f8a-417b-a5ae-84c4d832e9a0.jpg',
  'healthcare-administrative-operations-specialist-diploma':
    'https://cdn.magicpatterns.com/patterns/generated-images/e0442f7a-a3f3-4fef-8cf8-852c2c7ce4d2.jpg',
  'healthcare-administrative-operations-specialist-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/e0442f7a-a3f3-4fef-8cf8-852c2c7ce4d2.jpg',
  'medical-insurance-claims-administration-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/532a58fe-2f20-46d4-a419-7a5221d44690.jpg',
  'revenue-cycle-billing-specialist-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/63a30198-145f-4d9d-aed0-4a5ca2cce464.jpg',
  'radiologic-technology-bridge-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/845d834a-8241-42ed-9790-ab17d163f355.jpg',
  'clinical-medical-assisting-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/938f2136-8fa3-40ec-913e-56d7c4e37421.jpg',
  'medical-administrative-assistant-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/700b5614-de2b-48ac-9c21-8784c9474907.jpg',
  'medical-billing-coding-associate':
    'https://cdn.magicpatterns.com/patterns/generated-images/47ff826c-1637-4292-a15a-d34cde075746.jpg',
}

export function getProgramHeroImage(slug: string): string | undefined {
  return programHeroImage[slug]
}

export function getProgramMatch(program: ProgramTemplate): ProgramMatchContext {
  return {
    degreeInterest: degreeInterestFor(program.credential),
    areaStudy: domainAreaStudy[program.domainSlug],
    mProgram: programSpecialization[program.slug],
  }
}

export const programDomains: ProgramDomain[] = [
  {
    slug: 'business-leadership',
    name: 'Business Leadership',
    description:
      'Foundational business and leadership capabilities that apply across healthcare and service environments, preparing learners for management, operations, and process-oriented roles.',
  },
  {
    slug: 'community-population-health',
    name: 'Community, Population Health, & Care Coordination',
    description:
      'Programs that support health outcomes beyond individual encounters by emphasizing community-based care, population health awareness, and coordination across services and support systems.',
  },
  {
    slug: 'data-quality-ai',
    name: 'Data, Quality & AI Enablement',
    description:
      'The growing importance of data literacy, informatics, quality improvement, and emerging technology in healthcare — supporting roles that connect information, systems, and decision-making.',
  },
  {
    slug: 'healthcare-operations',
    name: 'Healthcare Operations',
    description:
      'Programs that strengthen the administrative and operational functions keeping healthcare organizations running efficiently, supporting both front-end coordination and day-to-day workflow management.',
  },
  {
    slug: 'financial-insurance-operations',
    name: 'Financial & Insurance Operations',
    description:
      'The financial and reimbursement functions of healthcare, preparing learners for roles that support billing accuracy, claims processing, and revenue cycle performance.',
  },
  {
    slug: 'clinical-care-delivery',
    name: 'Clinical Care Delivery',
    description:
      'Programs that support the delivery of patient care and help prepare learners for roles connected to clinical environments and diagnostic services.',
  },
]

export const programTemplates: ProgramTemplate[] = [
  {
    slug: 'business-management-associate',
    domainSlug: 'business-leadership',
    name: 'Business Management',
    credential: 'Associate Degree',
    metaTitle:
      'Business Management Associate Degree Online | Four Concentrations',
    metaDescription:
      'A fully online, 70-week Business Management associate degree with concentrations in Office and Process, Healthcare Management, Health Information Technology, and Project Management.',
    heroHeadline: 'Business Management',
    heroSubhead:
      'Prepare for entry-level supervisory and management careers across healthcare and non-healthcare industries — with four concentrations to match where you want to lead.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online, no labs or externship',
    overview: [
      'The Business Management Associate Degree program is a fully online, 70-week program designed to prepare students for entry-level supervisory and management careers across healthcare and non-healthcare industries. The program includes updated career readiness and student success content, including PULSE resources.',
      'The curriculum builds a strong foundation in management principles, organizational behavior, business communication, human resources, operations management, leadership, and performance improvement. Students develop practical problem-solving and decision-making skills through real-world business applications while emphasizing ethical leadership, teamwork, customer service excellence, and continuous process improvement.',
      'This program is designed for career-focused students who can work independently and seek assistance when needed. General education courses are completed early in the program to establish a strong academic foundation before concentration-specific coursework begins.',
    ],
    concentrations: [
      {
        name: 'Office and Process Management',
        description:
          'Business operations, employee supervision, budgeting fundamentals, and organizational effectiveness, preparing students for management roles in small to mid-sized organizations.',
      },
      {
        name: 'Healthcare Management',
        description:
          'Healthcare systems, medical office operations, regulatory compliance, patient services, and healthcare ethics to support leadership roles in healthcare environments.',
      },
      {
        name: 'Health Information Technology',
        description:
          'Oversee IT support functions, manage technology resources, coordinate technical teams, and align technology services with organizational goals.',
      },
      {
        name: 'Project Management',
        description:
          'Project scheduling, budgeting, risk management, team coordination, and project management tools and methodologies — aligned with CAPM certification preparation.',
      },
    ],
    features: [
      'Fully online format with no labs or externship requirements',
      'Four distinct concentrations with aligned coursework pathways',
      'Broad-based management skills applicable across industries',
      'Updated student success and career readiness content',
      'Project Management concentration aligned with CAPM certification standards',
    ],
    careersIntro:
      'Graduates are prepared for entry-level supervisory and management positions. The program emphasizes skills that are difficult to automate, including leadership, communication, supervision, and operational decision-making.',
    careerRoles: [
      'Office Manager',
      'Front-Line Supervisor',
      'Operations Coordinator',
      'Healthcare Office Supervisor',
      'Project Coordinator',
      'Administrative Leadership Roles',
    ],
  },
  {
    slug: 'community-health-associate',
    domainSlug: 'community-population-health',
    name: 'Community Health',
    credential: 'Associate Degree',
    metaTitle: 'Community Health Associate Degree Online | Population Health',
    metaDescription:
      'A fully online, 70-week Community Health associate degree focused on prevention, population health, health equity, and community outreach.',
    heroHeadline: 'Community Health',
    heroSubhead:
      'Prepare for careers in community health, health education, outreach, and prevention-focused roles that support healthier communities and improved population health outcomes.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online',
    overview: [
      'The Community Health Associate Degree program is a fully online, 70-week program that prepares students for careers in community health, health education, outreach, and prevention-focused roles.',
      'The curriculum emphasizes population health, health equity, prevention strategies, and the social determinants of health. Students develop foundational skills in health education, community engagement, needs assessment, program planning, and culturally responsive outreach. Through practical learning experiences, students gain experience using community health data, evaluating program effectiveness, and applying data-driven decision-making to support community-based initiatives.',
      'The program also focuses on ethical practice, professionalism, collaboration, and strong communication skills to prepare graduates to work effectively with interdisciplinary teams and diverse populations. Students learn how to engage individuals, families, and communities as active partners in improving health outcomes and increasing access to health information and services.',
    ],
    features: [
      'Fully online associate degree program',
      'Focus on prevention, population health, and health equity',
      'Emphasis on community engagement, outreach, and health education',
      'Training in needs assessment, program planning, and evaluation',
      'Development of culturally responsive communication and collaboration skills',
      'Preparation for community-based health and human services roles',
    ],
    studentProfile:
      'Designed for students interested in health and human services who are motivated to support and educate others within their communities. Successful students are typically independent learners with strong academic skills and an interest in continuing their education beyond entry-level roles — especially those drawn to teaching, training, care navigation, and community support services.',
    careersIntro:
      'Graduates are prepared for entry-level roles in community health, outreach, and health education settings. The program supports growing workforce needs in prevention programs and community-based health initiatives while addressing persistent disparities in health literacy, access, and public health resources.',
    careerRoles: [
      'Community Health Worker',
      'Outreach Specialist',
      'Health Educator Assistant',
      'Patient Navigator',
      'Community Support Coordinator',
    ],
    marketRelevance:
      'Demand for community-based health professionals continues to grow as organizations place greater emphasis on prevention, health education, and care coordination. Community engagement, trust-building, and culturally responsive communication remain highly valuable human-centered skills that are not easily automated, making this field an important and sustainable career pathway.',
  },
  {
    slug: 'healthcare-data-analytics-diploma',
    domainSlug: 'data-quality-ai',
    name: 'Healthcare Data Analytics',
    credential: 'Diploma',
    metaTitle: 'Healthcare Data Analytics Diploma Online | Health Information',
    metaDescription:
      'A fully online, 40-week Healthcare Data Analytics diploma covering healthcare informatics, EHRs, coding systems, data standards, reporting, and quality measurement.',
    heroHeadline: 'Healthcare Data Analytics',
    heroSubhead:
      'A shorter-term pathway into healthcare data and health information management, with a stackable route into the Health Informatics & AI Specialist associate degree.',
    weeks: 40,
    credits: 33,
    instructionalHours: 498,
    format: 'Fully online',
    overview: [
      'The Healthcare Data Analytics Diploma program is a fully online, 40-week program that prepares students for entry-level positions in healthcare data and health information management by developing foundational skills in healthcare informatics, electronic health records (EHRs), coding systems, data standards, reporting, and quality measurement.',
      'Students gain practical knowledge of healthcare data systems and workflows used across both clinical and administrative settings. The curriculum emphasizes the collection, validation, interpretation, and reporting of healthcare data to support informed decision-making, organizational performance improvement, and regulatory compliance.',
      'The program builds competency in medical terminology, standardized coding systems, data integrity, and data accuracy. Coursework focuses on ensuring healthcare documentation is complete, reliable, and compliant with industry standards, and develops an understanding of the legal and ethical responsibilities associated with the use, management, and release of health information.',
    ],
    features: [
      'Fully online diploma program',
      'Focus on healthcare informatics, EHRs, coding, and data standards',
      'Emphasis on data validation, reporting, and quality metrics',
      'Development of healthcare data literacy and analytical skills',
      'Preparation for compliance, quality reporting, and performance improvement initiatives',
      'Stackable credential into the Health Informatics & AI Specialist associate degree',
    ],
    studentProfile:
      'Designed for students interested in healthcare technology, data analysis, and health information systems. Especially suited for moderately strong academic students seeking a shorter-term pathway into healthcare with opportunities for future educational advancement.',
    careersIntro:
      'Graduates are prepared for entry-level healthcare data and health information roles, contributing to healthcare organizations through accurate data management, reporting, and performance measurement activities.',
    careerRoles: [
      'Health Information Technician',
      'Healthcare Data Coordinator',
      'EHR Support Specialist',
      'Quality Reporting Assistant',
      'Healthcare Records Analyst Support',
    ],
    marketRelevance:
      'Healthcare organizations increasingly rely on accurate data, analytics, and reporting to improve patient outcomes, operational efficiency, and regulatory compliance. As healthcare systems expand their use of data-driven technologies and informatics solutions, professionals with healthcare data analysis and reporting skills remain in high demand.',
  },
  {
    slug: 'health-informatics-ai-specialist-associate',
    domainSlug: 'data-quality-ai',
    name: 'Health Informatics & AI Specialist',
    credential: 'Associate Degree',
    metaTitle:
      'Health Informatics & AI Specialist Associate Degree Online | Healthcare Analytics',
    metaDescription:
      'A fully online, 70-week associate degree in health informatics and AI, covering healthcare data systems, analytics, interoperability, and responsible AI use.',
    heroHeadline: 'Health Informatics & AI Specialist',
    heroSubhead:
      'Prepare for entry-level careers where healthcare data, artificial intelligence, and clinical operations intersect.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online',
    overview: [
      'The Health Informatics and AI Specialist Associate Degree program is a fully online, 70-week program that prepares students for entry-level careers in healthcare data management, health informatics, analytics support, and healthcare technology environments.',
      'The curriculum provides a strong foundation in healthcare data systems, clinical documentation, medical terminology, coding literacy, reporting, and healthcare analytics. Students learn how to evaluate healthcare data for accuracy, quality, completeness, and regulatory compliance while developing practical skills in data analysis, validation, auditing, and interoperability.',
      'A key focus is the responsible use of AI-enabled tools and emerging analytics technologies to support healthcare decision-making and operational improvement. Students gain experience producing and interpreting healthcare reports while learning to maintain strong standards in data governance, privacy, security, ethics, and regulatory compliance.',
      'The program also introduces health information exchange and interoperability, helping students understand how healthcare systems communicate across clinical and administrative environments. Emphasis is placed on balancing technological innovation with human oversight and ethical responsibility.',
    ],
    features: [
      'Fully online associate degree program',
      'Focus on healthcare data systems, informatics, analytics, and AI applications',
      'Training in data analysis, validation, auditing, and interoperability',
      'Emphasis on responsible AI use, data governance, privacy, and security',
      'Development of healthcare reporting and applied analytics skills',
      'Exposure to AI-enabled analytics tools with emphasis on human oversight',
      'Preparation for healthcare data stewardship and quality-focused roles',
    ],
    studentProfile:
      'Designed for students interested in healthcare technology, health information management, data analytics, and emerging AI applications. Successful students are typically independent learners with strong academic skills who are adaptable, curious about evolving technologies, and interested in continuous learning.',
    careersIntro:
      'Graduates are prepared for entry-level roles in healthcare informatics, health information management, healthcare analytics support, and data quality operations.',
    careerRoles: [
      'Health Information Specialist',
      'Healthcare Data Analyst Support',
      'Clinical Data Coordinator',
      'Data Quality Specialist',
      'EHR Reporting Assistant',
      'Healthcare Informatics Support',
    ],
    marketRelevance:
      'As healthcare organizations increasingly rely on data analytics, interoperability, and AI-driven technologies to improve patient care and operational performance, demand grows for professionals who can manage, validate, interpret, and safeguard healthcare data. Value-based care, performance reporting, and AI-assisted technologies have created strong demand for people who understand both healthcare operations and responsible data management.',
  },
  {
    slug: 'healthcare-administrative-operations-specialist-diploma',
    domainSlug: 'healthcare-operations',
    name: 'Healthcare Administrative Operations Specialist',
    credential: 'Diploma',
    metaTitle:
      'Healthcare Administrative Operations Specialist Diploma Online | Patient Access',
    metaDescription:
      'A fully online, 40-week diploma in healthcare administrative operations covering patient access, scheduling, documentation, workflows, and EHRs.',
    heroHeadline: 'Healthcare Administrative Operations Specialist',
    heroSubhead:
      'A shorter-term pathway into healthcare administrative and operations support, with an embedded route into the associate degree.',
    weeks: 40,
    credits: 33,
    instructionalHours: 498,
    format: 'Fully online',
    overview: [
      'The Healthcare Administrative Operations Specialist Diploma program is a fully online, 40-week program that prepares students for entry-level healthcare administrative and operations support roles by building foundational knowledge in patient access services, scheduling, healthcare documentation, operational workflows, revenue cycle processes, and EHRs.',
      'The curriculum focuses on the day-to-day administrative systems and operational functions that support efficient healthcare delivery. Students learn how organizations manage patient intake, documentation flow, appointment coordination, information management, and communication across clinical and administrative departments.',
      'Students develop practical skills related to workflow coordination, healthcare operations support, compliance, privacy requirements, and ethical administrative practices — understanding how administrative processes contribute to safe, efficient, patient-centered healthcare operations.',
    ],
    features: [
      'Fully online diploma program',
      'Focus on patient access, scheduling, intake coordination, and healthcare workflows',
      'Emphasis on administrative operations support rather than clinical tasks',
      'Training in healthcare documentation, compliance, and information flow',
      'Development of communication, organization, and workflow coordination skills',
      'Embedded pathway into the associate degree program',
    ],
    studentProfile:
      'Designed for students interested in healthcare operations, administrative support, and healthcare information systems who are seeking a shorter-term pathway into the field. Successful students are typically moderately strong academic learners interested in organization, coordination, and healthcare workflows.',
    careersIntro:
      'Graduates are prepared for entry-level administrative and operations support positions within healthcare organizations. Advancement opportunities are especially strong within hospitals, large clinics, and expanding healthcare systems.',
    careerRoles: [
      'Healthcare Operations Coordinator',
      'Scheduling Coordinator',
      'Patient Access Support Specialist',
      'Administrative Operations Assistant',
      'Healthcare Office Support',
    ],
    marketRelevance:
      'Healthcare organizations continue to require skilled administrative professionals who can support operational efficiency, patient coordination, documentation accuracy, and regulatory compliance. As healthcare systems grow more complex and technology-driven, workflow coordination and operations support skills remain essential.',
  },
  {
    slug: 'healthcare-administrative-operations-specialist-associate',
    domainSlug: 'healthcare-operations',
    name: 'Healthcare Administrative Operations Specialist',
    credential: 'Associate Degree',
    metaTitle:
      'Healthcare Administrative Operations Specialist Associate Degree Online',
    metaDescription:
      'A fully online, 70-week associate degree in healthcare administrative operations covering healthcare systems, patient access, compliance, and operational coordination.',
    heroHeadline: 'Healthcare Administrative Operations Specialist',
    heroSubhead:
      'A comprehensive foundation in healthcare systems, patient access services, administrative workflows, compliance standards, and operational coordination.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online',
    overview: [
      'The Healthcare Administrative Operations Specialist Associate Degree program is a fully online, 70-week program that prepares students for healthcare administrative and operational management support roles.',
      'The curriculum emphasizes how healthcare organizations function from both administrative and operational perspectives. Students develop knowledge of healthcare delivery models, documentation standards, healthcare information systems, regulatory compliance, operational workflows, and data-informed decision-making. Coursework combines theoretical instruction with applied learning activities that reflect real-world administrative environments.',
      'Students gain practical experience in patient access processes, medical terminology, communication, healthcare documentation, operations coordination, and professional administrative practices. The program emphasizes the relationship between clinical services and administrative operations, and how efficient workflows, technology systems, and operational processes contribute to quality patient care.',
    ],
    features: [
      'Fully online associate degree program',
      'Focus on healthcare administrative and operational management',
      'Emphasis on patient access services, workflow coordination, and healthcare systems',
      'Training in healthcare technologies, documentation standards, and compliance',
      'Development of operational decision-making and professional communication skills',
      'Applied learning experiences reflecting real-world administrative environments',
      'Preparation for advancement within healthcare administration and operations pathways',
    ],
    studentProfile:
      'Designed for students interested in healthcare administration, operations management, healthcare systems, and technology-supported workflows. Successful students are typically independent learners with strong academic skills who are adaptable, organized, and interested in advancing into administrative leadership or operations-focused roles.',
    careersIntro:
      'Graduates are prepared for entry-level and advancing administrative operations roles, supporting operational accuracy, efficiency, communication, and patient-centered administrative services across a variety of healthcare settings.',
    careerRoles: [
      'Healthcare Operations Coordinator',
      'Patient Access Supervisor',
      'Healthcare Administrative Specialist',
      'Healthcare Office Manager Support',
      'Operational Workflow Coordinator',
    ],
    marketRelevance:
      'The healthcare industry continues to experience strong demand for professionals who understand healthcare operations, patient access systems, compliance standards, and administrative coordination. As organizations adapt to evolving technologies and regulatory requirements, this expertise remains critical to efficient healthcare delivery.',
  },
  {
    slug: 'medical-insurance-claims-administration-associate',
    domainSlug: 'financial-insurance-operations',
    name: 'Medical Insurance & Claims Administration',
    credential: 'Associate Degree',
    metaTitle:
      'Medical Insurance & Claims Administration Associate Degree Online',
    metaDescription:
      'A fully online, 70-week associate degree in insurance operations, claims administration, managed care, and payer-side healthcare support.',
    heroHeadline: 'Medical Insurance & Claims Administration',
    heroSubhead:
      'The first program with a payer-side focus — preparing you for insurance operations, claims administration, managed care, and member support roles.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online',
    overview: [
      'The Medical Insurance & Claims Administration Associate Degree program is a fully online, 70-week program that prepares students for careers in insurance operations, claims administration, managed care, and payer-side healthcare support roles within highly regulated environments.',
      'The curriculum focuses on health plan operations, eligibility and benefits administration, claims processing, enrollment and retention, prior authorization, utilization management, and member services. Students develop a strong understanding of insurance workflows and payer-side operations related to Medicare, Medicaid, Marketplace plans, and other coverage systems.',
      'Students learn to interpret benefits, apply coverage guidelines, navigate claims workflows, and support communication between providers, members, and insurance organizations. Coursework covers the full claims lifecycle, including adjudication, denials management, outcomes processing, and claims resolution, with emphasis on compliance, HIPAA requirements, documentation accuracy, and ethical administrative practices.',
      'The program also develops professional communication, customer service, and problem-solving skills needed to support positive member experiences and effective coordination within healthcare insurance systems.',
    ],
    features: [
      'Fully online associate degree program',
      'Focus on payer-side healthcare operations and managed care systems',
      'Training in claims processing, benefits administration, and utilization management',
      'Emphasis on regulatory compliance, HIPAA standards, and coverage guidelines',
      'Development of member services, communication, and problem-solving skills',
      'General education courses completed early in the program',
    ],
    studentProfile:
      'Designed for career-focused students interested in healthcare insurance operations, claims administration, managed care, and member support services. Successful students are typically moderate-to-strong academically, capable of working independently, and interested in administrative healthcare pathways with advancement opportunities in payer-side organizations.',
    careersIntro:
      'Graduates are prepared for entry-level roles in insurance and managed care organizations, with advancement opportunities within insurance administration and healthcare operations pathways.',
    careerRoles: [
      'Claims Processor',
      'Member Services Representative',
      'Prior Authorization Specialist',
      'Insurance Operations Coordinator',
      'Benefits Specialist Support',
      'Utilization Management Support Specialist',
      'Healthcare Payer Operations Associate',
    ],
    marketRelevance:
      'As healthcare systems expand and the population ages, demand grows for professionals who understand insurance operations, claims administration, and member support. Healthcare organizations and insurance providers increasingly seek workforce-ready professionals who can improve operational efficiency, regulatory compliance, claims accuracy, and member satisfaction.',
  },
  {
    slug: 'revenue-cycle-billing-specialist-associate',
    domainSlug: 'financial-insurance-operations',
    name: 'Revenue Cycle & Billing Specialist',
    credential: 'Associate Degree',
    metaTitle: 'Revenue Cycle & Billing Specialist Associate Degree Online',
    metaDescription:
      'A fully online, 70-week associate degree covering the complete revenue cycle — billing systems, payer processes, denial resolution, and compliance.',
    heroHeadline: 'Revenue Cycle & Billing Specialist',
    heroSubhead:
      'Build expertise in billing systems, payer processes, compliance standards, and financial operations across healthcare and service-based industries.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online',
    overview: [
      'The Revenue Cycle & Billing Specialist Associate Degree program is a fully online, 70-week program that prepares students for entry-level and advancing careers in revenue cycle operations across healthcare organizations and other service-based industries.',
      'The curriculum provides comprehensive training in the full revenue cycle process, including charge capture, claims submission, payment posting, accounts receivable management, denial resolution, collections strategies, insurance workflows, and revenue performance reporting.',
      'Coursework emphasizes the relationship between operational efficiency, financial sustainability, and patient or customer experience. Students develop practical skills in billing accuracy, dispute resolution, reimbursement processes, payer communication, and revenue recovery while gaining experience with reporting, compliance monitoring, and financial documentation workflows.',
      'The program also focuses on professional communication, analytical thinking, ethical practices, and data-informed decision-making, building technical proficiency and career readiness for revenue cycle management environments.',
    ],
    features: [
      'Fully online associate degree program',
      'Focus on the complete revenue cycle from billing through collections and recovery',
      'Training in accounts receivable, denial resolution, payer processes, and compliance',
      'Emphasis on billing accuracy, financial performance, and ethical standards',
      'Development of customer communication and dispute resolution skills',
      'Career pathways in both healthcare and non-healthcare service industries',
      'Alignment with optional CRCP certification preparation',
    ],
    studentProfile:
      'Designed for career-focused students interested in healthcare finance, billing operations, revenue management, and payer processes. Successful students are typically moderate-to-strong academically, capable of working independently, and interested in financial operations, compliance, customer communication, and operational problem-solving.',
    careersIntro:
      'Graduates are prepared for entry-level and advancing revenue cycle and billing roles, with pathways into supervisory and management-focused revenue cycle positions.',
    careerRoles: [
      'Revenue Cycle Specialist',
      'Medical Billing Specialist',
      'Accounts Receivable Coordinator',
      'Denial Management Specialist',
      'Insurance Billing Representative',
      'Reimbursement Specialist',
      'Collections Support Specialist',
      'Revenue Operations Coordinator',
    ],
    marketRelevance:
      'As healthcare organizations and service-based industries place increasing emphasis on compliance, financial accuracy, and operational efficiency, demand for skilled revenue cycle professionals continues to grow. While some billing functions are becoming automated, revenue recovery, denial resolution, customer communication, and dispute management continue to require strong analytical, technical, and interpersonal skills.',
  },
  {
    slug: 'radiologic-technology-bridge-associate',
    domainSlug: 'clinical-care-delivery',
    name: 'Radiologic Technology Bridge',
    credential: 'Associate Degree',
    metaTitle:
      'Radiologic Technology Bridge Associate Degree | For Limited Scope Radiographers',
    metaDescription:
      'A 70-week bridge associate degree for licensed Limited Scope Radiographers seeking ARRT Radiography certification eligibility, with four clinical rotations.',
    heroHeadline: 'Radiologic Technology Bridge',
    heroSubhead:
      'A completion pathway for licensed Limited Scope Radiographers advancing toward ARRT Radiography certification eligibility.',
    weeks: 70,
    credits: 60,
    instructionalHours: 1254,
    format: 'Online coursework with supervised clinicals',
    overview: [
      'The Radiologic Technology Bridge Associate Degree program is a 70-week completion program designed specifically for licensed Limited Scope Radiographers (LSRs) seeking to advance their education, expand their clinical expertise, and pursue eligibility for the ARRT Radiography certification exam.',
      'Building on prior limited scope radiography experience, the curriculum provides advanced education in diagnostic imaging, radiographic procedures, image analysis, patient care, radiographic physics, radiation protection, and the clinical competencies required for entry-level radiographers.',
      'The program combines online instruction with simulated laboratory experiences and supervised clinical rotations to strengthen technical proficiency, clinical judgment, and professional readiness. Students develop practical skills in safe imaging practices, patient communication, quality imaging procedures, and regulatory compliance.',
      'A strong emphasis is placed on patient safety, ethical standards, radiation protection, and diagnostic accuracy to prepare graduates for high-quality patient care and successful advancement within radiologic technology careers.',
    ],
    features: [
      'Bridge associate degree for licensed Limited Scope Radiographers',
      'Designed to support eligibility for the ARRT Radiography certification exam',
      'Online coursework, virtual labs, simulated learning, and supervised clinical experience',
      'Four clinical rotations totaling approximately 585 clinical hours',
      'Integrated ARRT exam preparation within the curriculum',
      'Focus on radiographic physics, image analysis, radiation protection, and patient care',
      'Clinical placement model based on targeted geographic areas and healthcare partnerships',
    ],
    studentProfile:
      'Designed for licensed Limited Scope Radiographers who already hold state certification or registration and at least one year of limited scope radiography experience. Successful students are highly motivated, academically strong, and committed to advancing in diagnostic imaging — independent learners with strong communication skills who can manage rigorous technical and clinical coursework.',
    careersIntro:
      'Graduates are prepared for advanced radiologic technology roles in hospitals, imaging centers, physician practices, and outpatient clinics. Successful ARRT certification and applicable state licensure may further expand opportunities.',
    careerRoles: [
      'Radiologic Technologist (X-ray Technologist)',
      'Diagnostic Imaging Technologist',
      'Radiology Technician',
    ],
    marketRelevance:
      'Demand for qualified radiologic technologists continues to grow as healthcare organizations expand diagnostic imaging services. Many markets experience workforce shortages, leading to strong hiring demand and advancement opportunities for credentialed technologists. The bridge pathway lets experienced Limited Scope Radiographers advance into broader imaging roles while meeting rising expectations for certification and clinical competency.',
  },
]

export interface CareerRoleDetail {
  /** What the role actually does, day to day. */
  summary: string
  /** Where the work typically happens. */
  setting: string
}

/**
 * Supporting copy for the roles listed in `careerRoles`, keyed by role title.
 * Kept in one place because several roles appear in more than one program, and
 * the description should not drift between them. Roles without an entry render
 * as a plain badge, so this map can stay partial.
 *
 * Deliberately free of salary figures and hiring guarantees — those vary by
 * market and employer, and the page points readers to their own research.
 */
export const careerRoleDetails: Record<string, CareerRoleDetail> = {
  // Business Leadership
  'Office Manager': {
    summary:
      'Runs the day-to-day operation of an office — staffing, scheduling, budgets, vendors, and the processes that keep the team productive.',
    setting: 'Small to mid-sized organizations',
  },
  'Front-Line Supervisor': {
    summary:
      'Leads a working team directly, handling assignments, performance coaching, and the escalations that come up during a shift.',
    setting: 'Service and operations teams',
  },
  'Operations Coordinator': {
    summary:
      'Keeps work moving across departments by tracking schedules, resources, and handoffs, and flagging bottlenecks before they spread.',
    setting: 'Operations and logistics teams',
  },
  'Healthcare Office Supervisor': {
    summary:
      'Supervises the administrative staff of a clinic or practice, balancing patient flow, compliance requirements, and team workload.',
    setting: 'Clinics and physician practices',
  },
  'Project Coordinator': {
    summary:
      'Supports a project manager by maintaining timelines, budgets, risk logs, and status reporting across the people doing the work.',
    setting: 'Project and program teams',
  },
  'Administrative Leadership Roles': {
    summary:
      'Advancement track into department-level administration, where the work shifts from running a process to setting how it runs.',
    setting: 'Across industries',
  },

  // Community, Population Health & Care Coordination
  'Community Health Worker': {
    summary:
      'Connects residents with health services and information, building the local trust that makes prevention programs actually reach people.',
    setting: 'Community organizations and public health',
  },
  'Outreach Specialist': {
    summary:
      'Plans and runs outreach events and campaigns, bringing screenings, education, and enrollment help into the community.',
    setting: 'Nonprofits and health systems',
  },
  'Health Educator Assistant': {
    summary:
      'Delivers health education sessions and materials, adapting content so it lands with the audience in front of them.',
    setting: 'Clinics, schools, and community programs',
  },
  'Patient Navigator': {
    summary:
      'Guides patients through appointments, referrals, and coverage questions so care does not stall between visits.',
    setting: 'Hospitals and clinics',
  },
  'Community Support Coordinator': {
    summary:
      'Coordinates services across agencies for individuals and families, tracking follow-through and closing gaps in support.',
    setting: 'Health and human services agencies',
  },

  // Data, Quality & AI Enablement
  'Health Information Technician': {
    summary:
      'Maintains patient records for accuracy, completeness, and compliance, and manages the release of health information.',
    setting: 'Hospitals and health information departments',
  },
  'Healthcare Data Coordinator': {
    summary:
      'Collects and validates clinical and operational data, preparing it for reporting and performance review.',
    setting: 'Health systems and quality teams',
  },
  'EHR Support Specialist': {
    summary:
      'Supports clinical and administrative staff using the electronic health record — access, workflows, documentation issues, and training.',
    setting: 'Hospitals and clinics',
  },
  'Quality Reporting Assistant': {
    summary:
      'Assembles the measure data behind quality and regulatory reporting, checking it against program definitions before submission.',
    setting: 'Quality and compliance departments',
  },
  'Healthcare Records Analyst Support': {
    summary:
      'Audits records against documentation standards and works with clinical teams to correct gaps.',
    setting: 'Health information management',
  },
  'Health Information Specialist': {
    summary:
      'Manages health information across its lifecycle — capture, integrity, retention, privacy, and authorized release.',
    setting: 'Hospitals and health systems',
  },
  'Healthcare Data Analyst Support': {
    summary:
      'Builds and interprets reports on clinical and operational performance, supporting the analysts and leaders who act on them.',
    setting: 'Analytics and informatics teams',
  },
  'Clinical Data Coordinator': {
    summary:
      'Manages clinical datasets and documentation workflows, keeping data consistent across systems and studies.',
    setting: 'Health systems and research settings',
  },
  'Data Quality Specialist': {
    summary:
      'Audits data for accuracy and completeness, traces errors to their source, and improves the workflows that produce them.',
    setting: 'Informatics and quality teams',
  },
  'EHR Reporting Assistant': {
    summary:
      'Produces recurring and ad hoc reports out of the electronic health record for clinical, operational, and compliance users.',
    setting: 'Hospitals and clinics',
  },
  'Healthcare Informatics Support': {
    summary:
      'Sits between clinical operations and technology, supporting the systems, interfaces, and data exchange that connect them.',
    setting: 'Health IT and informatics departments',
  },

  // Healthcare Operations
  'Healthcare Operations Coordinator': {
    summary:
      'Coordinates the administrative workflows behind care delivery — intake, scheduling, documentation flow, and interdepartmental handoffs.',
    setting: 'Hospitals and large clinics',
  },
  'Scheduling Coordinator': {
    summary:
      'Manages appointment and resource scheduling, balancing provider availability, room capacity, and patient needs.',
    setting: 'Clinics and outpatient centers',
  },
  'Patient Access Support Specialist': {
    summary:
      'Handles registration, insurance verification, and intake accuracy at the point where patients enter the system.',
    setting: 'Hospital patient access departments',
  },
  'Administrative Operations Assistant': {
    summary:
      'Supports daily operations with documentation, correspondence, records, and the coordination that keeps a department running.',
    setting: 'Healthcare organizations',
  },
  'Healthcare Office Support': {
    summary:
      'Provides front-office support — patient communication, records handling, and administrative follow-through.',
    setting: 'Medical offices and practices',
  },
  'Patient Access Supervisor': {
    summary:
      'Leads a patient access team, owning registration accuracy, insurance verification quality, and front-end revenue integrity.',
    setting: 'Hospitals and health systems',
  },
  'Healthcare Administrative Specialist': {
    summary:
      'Handles the more complex administrative work of a department — compliance documentation, process improvement, and reporting.',
    setting: 'Healthcare organizations',
  },
  'Healthcare Office Manager Support': {
    summary:
      'Works alongside an office manager on staffing, scheduling, vendor coordination, and operational reporting.',
    setting: 'Clinics and practices',
  },
  'Operational Workflow Coordinator': {
    summary:
      'Maps and improves how work moves through a department, reducing rework and handoff delays.',
    setting: 'Healthcare operations teams',
  },

  // Financial & Insurance Operations
  'Claims Processor': {
    summary:
      'Reviews and adjudicates claims against coverage rules, resolving the errors and mismatches that hold up payment.',
    setting: 'Insurance and managed care organizations',
  },
  'Member Services Representative': {
    summary:
      'Answers member questions on coverage, benefits, and claims, and resolves issues across plan systems.',
    setting: 'Health plans and payer organizations',
  },
  'Prior Authorization Specialist': {
    summary:
      'Processes authorization requests against medical policy, coordinating between providers and the plan on documentation.',
    setting: 'Health plans and utilization management',
  },
  'Insurance Operations Coordinator': {
    summary:
      'Coordinates the operational workflows of a health plan — enrollment, eligibility, and the handoffs between them.',
    setting: 'Payer organizations',
  },
  'Benefits Specialist Support': {
    summary:
      'Interprets and applies benefit plan designs, supporting eligibility determinations and coverage questions.',
    setting: 'Health plans and benefits administration',
  },
  'Utilization Management Support Specialist': {
    summary:
      'Supports utilization review by gathering clinical documentation and tracking cases against review timelines.',
    setting: 'Managed care organizations',
  },
  'Healthcare Payer Operations Associate': {
    summary:
      'Works across payer operations — claims, enrollment, and member support — with an eye on compliance and accuracy.',
    setting: 'Insurance organizations',
  },
  'Revenue Cycle Specialist': {
    summary:
      'Works the full revenue cycle from charge capture through payment, chasing down what is unpaid and why.',
    setting: 'Hospitals and physician groups',
  },
  'Medical Billing Specialist': {
    summary:
      'Prepares and submits claims, posts payments, and corrects the billing errors that cause denials.',
    setting: 'Healthcare providers and billing companies',
  },
  'Medical Coder': {
    summary:
      'Reads clinical documentation and assigns the ICD-10, CPT, and HCPCS codes that claims are built from.',
    setting: 'Physician practices, hospitals, and coding services',
  },
  'Coding Specialist': {
    summary:
      'Handles complex and specialty coding, audits assigned codes, and queries providers when documentation is unclear.',
    setting: 'Hospitals and health information management teams',
  },
  'Accounts Receivable Coordinator': {
    summary:
      'Monitors aging receivables, follows up with payers, and escalates accounts at risk of write-off.',
    setting: 'Healthcare finance departments',
  },
  'Denial Management Specialist': {
    summary:
      'Analyzes denied claims, builds appeals, and feeds root causes back upstream so the same denials stop recurring.',
    setting: 'Revenue cycle departments',
  },
  'Insurance Billing Representative': {
    summary:
      'Handles payer-facing billing communication, from claim status inquiries through resolution.',
    setting: 'Providers and billing services',
  },
  'Reimbursement Specialist': {
    summary:
      'Reviews reimbursement against contracts and fee schedules, identifying underpayments and recovering them.',
    setting: 'Healthcare finance teams',
  },
  'Collections Support Specialist': {
    summary:
      'Works patient and payer balances with clear, compliant communication and workable payment arrangements.',
    setting: 'Healthcare and service industries',
  },
  'Revenue Operations Coordinator': {
    summary:
      'Coordinates the people, systems, and reporting behind revenue performance across the cycle.',
    setting: 'Healthcare organizations',
  },

  // Clinical Care Delivery
  'Radiologic Technologist (X-ray Technologist)': {
    summary:
      'Performs diagnostic imaging exams, positioning patients and equipment for image quality while managing radiation safety.',
    setting: 'Hospitals and imaging centers',
  },
  'Diagnostic Imaging Technologist': {
    summary:
      'Produces diagnostic images across modalities, working from physician orders and clinical protocols.',
    setting: 'Imaging centers and hospitals',
  },
  'Radiology Technician': {
    summary:
      'Supports radiology operations — patient preparation, equipment handling, imaging workflow, and safety procedures.',
    setting: 'Outpatient clinics and physician practices',
  },
}

/**
 * Medical assisting programs. These sit alongside the six workforce domains
 * above and are the three credentials most often searched by name, so each
 * gets its own page rather than being folded into a broader program.
 */
programTemplates.push(
  {
    slug: 'clinical-medical-assisting-associate',
    domainSlug: 'clinical-care-delivery',
    name: 'Clinical Medical Assisting',
    credential: 'Associate Degree',
    metaTitle:
      'Clinical Medical Assisting Associate Degree Online | Accredited Programs',
    metaDescription:
      'Compare accredited online clinical medical assisting associate degree programs. Learn patient care skills, clinical procedures, and prepare for CMA certification.',
    heroHeadline: 'Clinical Medical Assisting',
    heroSubhead:
      'Train for hands-on patient care in clinics, physician practices, and outpatient settings — the clinical side of medical assisting, from vital signs to phlebotomy.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Online coursework with a hands-on externship',
    overview: [
      'The Clinical Medical Assisting Associate Degree program prepares students for the patient-facing side of medical assisting. Coursework covers clinical procedures, patient intake and assessment, medical terminology, anatomy and physiology, pharmacology basics, and infection control, building toward the competencies expected of an entry-level clinical medical assistant.',
      'Students learn to take and record vital signs, assist providers during examinations and minor procedures, perform phlebotomy and specimen collection, run basic laboratory tests, administer medications under supervision, and record EKGs. Alongside the clinical skills, the curriculum covers documentation in electronic health records, HIPAA and patient privacy, and professional communication with patients and care teams.',
      'Because clinical medical assisting is a hands-on role, coursework is paired with supervised practical experience. Most accredited programs require an externship at a partner healthcare site so students apply their training in a real clinical environment before graduating. Externship requirements vary by school — ask each program how placement works in your area.',
    ],
    features: [
      'Online coursework paired with supervised hands-on clinical training',
      'Instruction in vital signs, phlebotomy, EKG, and specimen collection',
      'Electronic health record documentation and HIPAA compliance',
      'Preparation for CMA (AAMA) or RMA certification after graduation',
      'Accreditation through CAAHEP or ABHES at participating schools',
      'Pathway into nursing and allied health programs for students who continue',
    ],
    studentProfile:
      'This program suits students who want direct patient contact rather than a desk role, and who are comfortable balancing online coursework with an in-person externship. Strong communication skills and attention to detail matter more here than prior healthcare experience.',
    careersIntro:
      'Graduates are prepared for entry-level clinical roles in physician practices, outpatient clinics, urgent care, and specialty offices. Employers typically look for a credential plus demonstrated clinical competency.',
    careerRoles: [
      'Clinical Medical Assistant',
      'Certified Medical Assistant (CMA)',
      'Patient Care Technician',
      'Clinical Care Assistant',
      'Phlebotomy Technician',
      'EKG Technician',
    ],
    marketRelevance:
      'Medical assisting remains one of the most consistently hired allied health roles, driven by growth in outpatient and ambulatory care. Because the work combines direct patient interaction with clinical procedures, it is among the healthcare roles least exposed to automation. Check current Bureau of Labor Statistics data for employment projections in your state.',
  },
  {
    slug: 'medical-administrative-assistant-associate',
    domainSlug: 'healthcare-operations',
    name: 'Medical Administrative Assistant',
    credential: 'Associate Degree',
    metaTitle:
      'Medical Administrative Assistant Associate Degree Online | Accredited Programs',
    metaDescription:
      'Compare accredited online medical administrative assistant associate degree programs. Learn scheduling, patient records, insurance basics, and medical office operations.',
    heroHeadline: 'Medical Administrative Assistant',
    heroSubhead:
      'Run the front of a medical practice — scheduling, patient records, insurance verification, and the daily coordination that keeps a clinic moving.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online, no externship required',
    overview: [
      'The Medical Administrative Assistant Associate Degree program prepares students for the administrative and operational side of healthcare delivery. The curriculum covers patient scheduling, registration and intake, medical records management, insurance verification, and the front-office workflows that connect patients to care.',
      'Students build a working knowledge of medical terminology, anatomy, healthcare documentation standards, and the coding and billing basics an administrative assistant encounters daily. Coursework also covers electronic health record systems, HIPAA and privacy requirements, and the compliance standards that govern how patient information is handled.',
      'Beyond the technical material, the program develops the communication and organizational skills the role depends on — handling patient questions with professionalism, coordinating between clinical and administrative staff, managing competing priorities at a busy front desk, and maintaining accuracy in records that clinical decisions rely on.',
    ],
    features: [
      'Fully online with no externship requirement',
      'Training in scheduling, registration, and patient intake workflows',
      'Electronic health records, HIPAA, and documentation standards',
      'Insurance verification and introductory billing and coding',
      'Emphasis on patient communication and front-office professionalism',
      'Preparation for CMAA certification at participating schools',
    ],
    studentProfile:
      'This program suits organized, detail-oriented students who want a healthcare career without clinical or patient-care tasks. Because it requires no externship, it works well for students who need a fully online schedule around work or family.',
    careersIntro:
      'Graduates are prepared for administrative roles across physician practices, hospitals, clinics, and specialty offices, with advancement paths into office management and healthcare operations.',
    careerRoles: [
      'Medical Administrative Assistant',
      'Patient Services Representative',
      'Medical Secretary',
      'Scheduling Coordinator',
      'Patient Access Representative',
      'Medical Office Coordinator',
    ],
    marketRelevance:
      'Healthcare organizations continue to need administrative staff who understand both clinical vocabulary and operational workflow. As practices adopt more electronic systems, employers increasingly value administrative assistants who can navigate EHR platforms and maintain documentation accuracy under compliance requirements.',
  },
  {
    slug: 'medical-billing-coding-associate',
    domainSlug: 'financial-insurance-operations',
    name: 'Medical Billing & Coding',
    credential: 'Associate Degree',
    metaTitle:
      'Medical Billing and Coding Associate Degree Online | Accredited Programs',
    metaDescription:
      'Compare accredited online medical billing and coding associate degree programs. Learn ICD-10, CPT, HCPCS, claims processing, and prepare for CPC or CCA certification.',
    heroHeadline: 'Medical Billing & Coding',
    heroSubhead:
      'Translate clinical care into the codes and claims that get providers paid — a fully online path into healthcare with no patient-facing requirement.',
    weeks: 70,
    credits: 60,
    instructionalHours: 903,
    format: 'Fully online, no externship required',
    overview: [
      'The Medical Billing & Coding Associate Degree program trains students to convert clinical documentation into standardized codes and to manage the claims that follow. The curriculum covers the ICD-10-CM, CPT, and HCPCS Level II code sets, along with the medical terminology, anatomy, and pathophysiology needed to read a clinical record accurately.',
      'Students work through the full claims lifecycle: abstracting diagnoses and procedures from documentation, assigning and sequencing codes, applying payer-specific rules, submitting claims, posting payments, and resolving denials. Coursework also covers reimbursement methodologies, insurance types including Medicare and Medicaid, and the compliance and auditing standards that govern coding accuracy.',
      'A significant portion of the program addresses the consequences of getting it wrong. Students learn how coding errors create denials, delay payment, and expose organizations to compliance risk, and how careful documentation review, query processes, and internal auditing prevent those outcomes.',
    ],
    features: [
      'Fully online with no externship or clinical hours',
      'Full coverage of ICD-10-CM, CPT, and HCPCS Level II code sets',
      'Complete claims lifecycle from abstraction through denial resolution',
      'Compliance, auditing, and documentation integrity standards',
      'Preparation for CPC (AAPC) or CCA (AHIMA) certification',
      'Applicable across provider offices, hospitals, and payer organizations',
    ],
    studentProfile:
      'This program suits analytical students who prefer detailed, rules-based work over patient contact. Success depends on precision and persistence rather than clinical aptitude, and the fully online format fits students working around other commitments.',
    careersIntro:
      'Graduates are prepared for coding and billing roles in provider offices, hospitals, billing companies, and insurance organizations. Most employers expect or strongly prefer professional certification alongside the degree.',
    careerRoles: [
      'Medical Coder',
      'Medical Billing Specialist',
      'Coding Specialist',
      'Claims Processor',
      'Reimbursement Specialist',
      'Accounts Receivable Coordinator',
    ],
    marketRelevance:
      'Coding and billing accuracy directly affects whether healthcare organizations get paid, which keeps demand steady. While automated coding tools handle routine encounters, complex cases, denial resolution, auditing, and compliance review continue to require trained human judgment — and those are the areas where certified coders are most valuable.',
  },
)

export function getCareerRoleDetail(role: string): CareerRoleDetail | undefined {
  return careerRoleDetails[role]
}

export const programsIndexSlug = 'new-online-programs'

export function getProgramTemplate(slug: string): ProgramTemplate | undefined {
  const normalized = slug.toLowerCase()
  return programTemplates.find((p) => p.slug.toLowerCase() === normalized)
}

export function getProgramsByDomain(domainSlug: string): ProgramTemplate[] {
  return programTemplates.filter((p) => p.domainSlug === domainSlug)
}

export function getDomain(slug: string): ProgramDomain | undefined {
  return programDomains.find((d) => d.slug === slug)
}
